import type { ReactNode } from "react"

import { CircleHelp, LogOut, Moon, Sun } from "lucide-react"

import { Button } from "@/components/ui/button"
import { LANGS, useI18n } from "@/i18n"
import { useAppConfig, wordmarkParts } from "@/lib/app-config"
import { useIdentity } from "@/lib/identity"
import { useTheme } from "@/providers/ThemeProvider"

/** Official celver logo: black on light backgrounds, white on dark ones, per the CI. */
export function CelverLogo({ className = "" }: { className?: string }) {
  return (
    <>
      <img src="/brand/celver-logo-black.svg" alt="celver" className={`w-auto dark:hidden ${className}`} />
      <img src="/brand/celver-logo-white.svg" alt="celver" className={`hidden w-auto dark:block ${className}`} />
    </>
  )
}

/** The logo, a divider and the agent title set semibold. */
export function Wordmark({ className = "" }: { className?: string }) {
  const [, product] = wordmarkParts(useAppConfig().title)
  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      <CelverLogo className="h-5" />
      <span className="h-4 w-px bg-border" />
      <span className="text-[15px] font-semibold text-fg2">{product}</span>
    </div>
  )
}

export function Header({
  menuSlot,
  onOpenHelp,
  helpUnseen = false,
}: {
  menuSlot?: ReactNode
  onOpenHelp?: () => void
  helpUnseen?: boolean
}) {
  const { t, lang, setLang } = useI18n()
  const { user: account, signOut: logout } = useIdentity()
  const { resolvedTheme, setTheme } = useTheme()

  return (
    <header className="flex h-15 shrink-0 items-center justify-between border-b border-border-subtle bg-card px-4">
      <div className="flex items-center gap-2.5">
        {menuSlot}
        <Wordmark />
      </div>

      <div className="flex items-center gap-1.5">
        {account && (
          <span className="mr-1 hidden rounded-lg bg-elevated px-3 py-1 text-xs text-fg3 sm:block">
            {account.name ?? account.username}
          </span>
        )}
        {onOpenHelp && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onOpenHelp}
            aria-label={helpUnseen ? t("header.guideNew") : t("header.guide")}
            title={t("header.guide")}
            className="relative"
          >
            <CircleHelp className="size-4" />
            {helpUnseen && <span className="absolute top-1.5 right-1.5 size-2 rounded-full bg-orange" aria-hidden="true" />}
          </Button>
        )}
        <div role="group" aria-label={t("header.language")} className="flex items-center rounded-md border border-border-subtle p-0.5">
          {LANGS.map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => setLang(code)}
              aria-pressed={lang === code}
              aria-label={code === "en" ? t("header.langEnglish") : t("header.langGerman")}
              title={code === "en" ? t("header.langEnglish") : t("header.langGerman")}
              className={`cursor-pointer rounded px-1.5 py-0.5 text-[11px] font-semibold ${
                lang === code ? "bg-orange text-on-orange" : "text-fg3 hover:bg-hover hover:text-foreground"
              }`}
            >
              {code.toUpperCase()}
            </button>
          ))}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
          aria-label={t("header.toggleTheme")}
        >
          <Sun className="size-4 scale-100 rotate-0 transition-all dark:scale-0 dark:-rotate-90" />
          <Moon className="absolute size-4 scale-0 rotate-90 transition-all dark:scale-100 dark:rotate-0" />
        </Button>
        {account && (
          <Button
            variant="ghost"
            size="icon"
            onClick={logout}
            aria-label={t("header.signOut")}
            className="text-muted-foreground hover:text-destructive"
          >
            <LogOut className="size-4" />
          </Button>
        )}
      </div>
    </header>
  )
}
